/**
 * `html.touch`: level-2 mega menu uses taps only (no hover).
 * `html.no-touch`: hover switches panels.
 */
function useClickOnlyMegaNav() {
  return typeof window !== 'undefined' && document.documentElement.classList.contains('touch');
}

class MegaMenuElement extends HTMLElement {
  /** @type {NodeListOf<HTMLElement>} */
  #items = this.querySelectorAll('[aria-controls^="level3-"]');

  /** @type {HTMLElement|null} */
  #activeItem = [...this.#items].find((item) => item.ariaExpanded === 'true') ?? null;

  /** @type {HTMLElement|null} */
  #activeTarget = this.#activeItem === null ? null : this.#getTargetForItem(this.#activeItem);

  /** @type {HTMLElement|null} */
  #contentWrapper = null;

  /** @type {HTMLElement|null} */
  #minHeight = this.offsetHeight;

  connectedCallback() {
    this.#contentWrapper = this.querySelector('[data-mega-menu-content-wrapper]');
    this.#items.forEach((item) => {
      item.addEventListener('mouseenter', () => this.#handleMouseEnter(item));
      item.addEventListener('click', (event) => this.#handleItemClick(event, item), {
        capture: true,
      });
      item.addEventListener('keydown', (event) => {
        if (event.code !== 'Space') return;
        event.preventDefault();
        this.#activateItem(item);
      });
    });
    this.#updateContentWrapperMinHeight();
  }

  #handleMouseEnter(item) {
    if (useClickOnlyMegaNav()) return;
    this.#activateItem(item);
  }

  /**
   * Touch: first tap switches panel only; second tap on the same row follows `href`.
   * Rows without a level-3 panel behave as normal links.
   */
  #handleItemClick(event, item) {
    if (!useClickOnlyMegaNav()) return;

    const target = this.#getTargetForItem(item);
    if (!target) return;

    if (item.getAttribute('aria-expanded') !== 'true') {
      event.preventDefault();
      this.#activateItem(item);
    }
  }

  #activateItem(item) {
    const target = this.#getTargetForItem(item);
    const isAlreadyActive =
      item === this.#activeItem && target !== null && target === this.#activeTarget;

    if (isAlreadyActive) return;

    this.#collapseAllItemTargets();

    if (!target) {
      this.#activeTarget = null;
      this.#activeItem = null;
      this.#updateContentWrapperMinHeight();
      return;
    }

    target.ariaExpanded = 'true';
    item.ariaExpanded = 'true';

    this.#activeTarget = target;
    this.#activeItem = item;

    this.#updateContentWrapperMinHeight();
  }

  #collapseAllItemTargets() {
    this.#items.forEach((item) => {
      item.ariaExpanded = 'false';
      const target = this.#getTargetForItem(item);
      if (target) target.ariaExpanded = 'false';
    });
  }

  #updateContentWrapperMinHeight() {
    const wrapper = this.#contentWrapper;
    if (!wrapper) return;

    if (!this.#activeTarget) {
      wrapper.style.minHeight = `${this.#minHeight}px`;
      return;
    }

    const activeTarget = this.#activeTarget;
    requestAnimationFrame(() => {
      if (this.#activeTarget !== activeTarget) return;
      const height = activeTarget.offsetHeight;
      wrapper.style.minHeight = height > 0 ? `${height}px` : '';
    });
  }

  /**
   * @param {HTMLElement} item
   * @returns {HTMLElement|null}
   */
  #getTargetForItem(item) {
    const targetId = item.getAttribute('aria-controls');
    if (targetId === null || targetId === '') return null;

    return this.querySelector(`#${CSS.escape(targetId)}`);
  }

  collapseAll() {
    this.#collapseAllItemTargets();
    this.#activeTarget = null;
    this.#activeItem = null;
    this.#updateContentWrapperMinHeight();
  }

  expandDefaultPanel() {
    this.#collapseAllItemTargets();

    const firstWithTarget = [...this.#items].find((item) => this.#getTargetForItem(item));
    if (!firstWithTarget) {
      this.#activeItem = null;
      this.#activeTarget = null;
      this.#updateContentWrapperMinHeight();
      return;
    }

    const target = this.#getTargetForItem(firstWithTarget);
    if (!target) {
      this.#activeItem = null;
      this.#activeTarget = null;
      this.#updateContentWrapperMinHeight();
      return;
    }

    firstWithTarget.ariaExpanded = 'true';
    target.ariaExpanded = 'true';
    this.#activeItem = firstWithTarget;
    this.#activeTarget = target;
    this.#updateContentWrapperMinHeight();
  }
}

customElements.define('mega-menu-element', MegaMenuElement);
